// Lógica pura del catálogo de reportes del Admin SK: cruza el catálogo declarado con lo que responde
// el motor del lake. Sin React, sin fetch y sin `Date.now()` implícito.
import { ApiError } from '@/api/errors';
import type {
    CatalogTableWire,
    ReportJobStatus,
    ReportJobWire,
} from '@/api/services/datalake-query';
import {
    JOBS_HISTORY_LIMIT,
    ReportsServiceNotConfiguredError,
} from '@/api/services/datalake-query';
import { NO_RUN_PERMISSION_HINT } from './permissions';
import type {
    ReportCard,
    ReportCategory,
    ReportDefinition,
    ReportKpis,
    ReportPeriodicity,
    ReportRunEntry,
} from './types';

const DAY_MS = 24 * 60 * 60 * 1000;
const KPI_WINDOW_DAYS = 30;

/** Orden de las secciones tal como las numera el mock (§1 a §4). */
export const CATEGORY_ORDER: ReportCategory[] = [
    'REGULATORY',
    'PORTFOLIO',
    'COLLECTIONS',
    'ACCOUNTING',
];

/** Encabezado de cada sección, literal del mock. */
export const CATEGORY_TITLE: Record<ReportCategory, string> = {
    REGULATORY: 'Reportes regulatorios · atención especial',
    PORTFOLIO: 'Cartera',
    COLLECTIONS: 'Cobranza',
    ACCOUNTING: 'Contables',
};

/** Texto a la derecha del encabezado, literal del mock. */
export const CATEGORY_META: Record<ReportCategory, string> = {
    REGULATORY: 'Obligatorios; incumplimiento tiene consecuencia legal o de reputación',
    PORTFOLIO: 'Saldo, mora, cosecha, originación',
    COLLECTIONS: 'Cobros por canal, rendimiento cobrador, bloqueo',
    ACCOUNTING: 'Bajas contables y recuperación',
};

/** Etiqueta del chip de categoría en el detalle. */
export const CATEGORY_LABEL: Record<ReportCategory, string> = {
    REGULATORY: 'Regulatorio',
    PORTFOLIO: 'Cartera',
    COLLECTIONS: 'Cobranza',
    ACCOUNTING: 'Contable',
};

export const PERIODICITY_LABEL: Record<ReportPeriodicity, string> = {
    DAILY: 'Diaria',
    WEEKLY: 'Semanal',
    MONTHLY: 'Mensual',
};

export const PERIODICITY_CHIP: Record<ReportPeriodicity, string> = {
    DAILY: 'bg-info-soft text-info',
    WEEKLY: 'bg-success-soft text-success',
    MONTHLY: 'bg-warning-soft text-warning',
};

export const JOB_STATUS_LABEL: Record<ReportJobStatus, string> = {
    RUNNING: 'Generando',
    DONE: 'Lista',
    FAILED: 'Falló',
};

export const JOB_STATUS_CHIP: Record<ReportJobStatus, string> = {
    RUNNING: 'bg-info-soft text-info',
    DONE: 'bg-success-soft text-success',
    FAILED: 'bg-destructive/10 text-destructive',
};

/** Motivo de bloqueo de un reporte que no tiene tabla en el lake. */
export const NO_LAKE_TABLE_REASON =
    'El data lake todavía no publica una tabla que sostenga este reporte, así que no hay nada que consultar.';

/** `1,420`: miles con separador mexicano. */
export function formatInt(value: number): string {
    return value.toLocaleString('es-MX');
}

/** Ordena por `reportId`, que es a la vez alfabético y el orden del documento. */
export function sortDefinitions(definitions: readonly ReportDefinition[]): ReportDefinition[] {
    return [...definitions].sort((a, b) => a.reportId.localeCompare(b.reportId));
}

// Qué puede mostrar la pantalla según la configuración y la respuesta del catálogo del lake.
// `unavailable` = sin servicio, `forbidden` = sin permiso, `failed` = el servicio falló.
export type ReportsAvailability = 'loading' | 'ready' | 'unavailable' | 'forbidden' | 'failed';

export interface ReportsAvailabilityInput {
    configured: boolean;
    isSuccess: boolean;
    error: unknown;
}

/** Un 403 sin código Fiado viene de API Gateway, que así responde a una ruta que no existe. */
function hasFiadoCode(error: ApiError): boolean {
    return Boolean(error.code) && error.code !== 'UNKNOWN_ERROR';
}

/** Clasifica el estado del servicio de reportes; ver `ReportsAvailability`. */
export function resolveReportsAvailability({
    configured,
    isSuccess,
    error,
}: ReportsAvailabilityInput): ReportsAvailability {
    if (!configured || error instanceof ReportsServiceNotConfiguredError) return 'unavailable';
    if (error instanceof ApiError) {
        if (error.httpStatus === 404) return 'unavailable';
        if (error.httpStatus === 403) return hasFiadoCode(error) ? 'forbidden' : 'unavailable';
        return 'failed';
    }
    if (error) return 'failed';
    return isSuccess ? 'ready' : 'loading';
}

/** Retry de las queries del motor: nunca en 4xx ni sin configurar; una sola vez en lo demás. */
export function shouldRetryReportsQuery(failureCount: number, error: unknown): boolean {
    if (error instanceof ReportsServiceNotConfiguredError) return false;
    if (error instanceof ApiError && error.httpStatus >= 400 && error.httpStatus < 500)
        return false;
    return failureCount < 1;
}

/** El job más reciente de esa tabla, opcionalmente acotado a un estado. */
function latestJobFor(
    jobs: readonly ReportJobWire[],
    table: string,
    status?: ReportJobStatus,
): ReportJobWire | null {
    return jobs.reduce<ReportJobWire | null>((best, candidate) => {
        if (candidate.table !== table) return best;
        if (status && candidate.status !== status) return best;
        return !best || candidate.createdAt > best.createdAt ? candidate : best;
    }, null);
}

export interface BuildReportCardsInput {
    definitions: readonly ReportDefinition[];
    /** Las tablas que devuelve `GET /catalog`. */
    tables: readonly CatalogTableWire[];
    jobs: readonly ReportJobWire[];
    canRun: boolean;
}

/** Cruza cada definición con el catálogo del tenant y su historial; el permiso bloquea primero. */
export function buildReportCards({
    definitions,
    tables,
    jobs,
    canRun,
}: BuildReportCardsInput): ReportCard[] {
    return sortDefinitions(definitions).map((definition) => {
        const { lakeTable } = definition;
        const catalogEntry = lakeTable
            ? tables.find((entry) => entry.qualifiedName === lakeTable)
            : undefined;

        let blockedReason: string | null = null;
        if (!canRun) {
            blockedReason = NO_RUN_PERMISSION_HINT;
        } else if (!lakeTable) {
            blockedReason = NO_LAKE_TABLE_REASON;
        } else if (!catalogEntry) {
            blockedReason = `La tabla ${lakeTable} no está en el catálogo que este tenant puede consultar.`;
        } else if (!catalogEntry.queryable) {
            blockedReason =
                catalogEntry.reason ?? `La tabla ${lakeTable} no es consultable para este tenant.`;
        }

        const lastRun = lakeTable ? latestJobFor(jobs, lakeTable) : null;
        const lastDone = lakeTable ? latestJobFor(jobs, lakeTable, 'DONE') : null;

        return {
            definition,
            blockedReason,
            lastRunAt: lastRun?.createdAt ?? null,
            lastRunStatus: lastRun?.status ?? null,
            downloadableJobId: lastDone?.jobId ?? null,
        };
    });
}

export interface ReportCategoryGroup {
    category: ReportCategory;
    cards: ReportCard[];
}

/** Devuelve siempre los cuatro grupos, aunque alguno quede vacío. */
export function groupCardsByCategory(cards: readonly ReportCard[]): ReportCategoryGroup[] {
    return CATEGORY_ORDER.map((category) => ({
        category,
        cards: cards.filter((card) => card.definition.category === category),
    }));
}

/** Arma el log de corridas, de la más reciente a la más vieja. */
export function buildRunEntries(
    jobs: readonly ReportJobWire[],
    definitions: readonly ReportDefinition[],
): ReportRunEntry[] {
    return [...jobs]
        .sort((a, b) => b.createdAt - a.createdAt)
        .map((job) => {
            const match = definitions.find((definition) => definition.lakeTable === job.table);
            return {
                jobId: job.jobId,
                reportName: match?.name ?? null,
                reportId: match?.reportId ?? null,
                table: job.table,
                createdAt: job.createdAt,
                status: job.status,
                failureReason: job.failureReason ?? null,
            };
        });
}

/** Los cinco contadores de la barra: cuatro del catálogo declarado y uno del historial. */
export function computeReportKpis(
    definitions: readonly ReportDefinition[],
    jobs: readonly ReportJobWire[],
    now: number,
): ReportKpis {
    const since = now - KPI_WINDOW_DAYS * DAY_MS;
    return {
        total: definitions.length,
        regulatory: definitions.filter((report) => report.category === 'REGULATORY').length,
        daily: definitions.filter((report) => report.periodicity === 'DAILY').length,
        monthly: definitions.filter((report) => report.periodicity === 'MONTHLY').length,
        runsLast30d: jobs.filter((job) => job.createdAt >= since).length,
        runsTruncated: jobs.length >= JOBS_HISTORY_LIMIT,
    };
}
